import React, { useEffect, useMemo, useState } from 'react'

import axios from 'axios'
import { toast } from 'react-toastify'
import {
  FaCamera,
  FaImage,
  FaMapMarkerAlt,
  FaPlus,
  FaTimes
} from 'react-icons/fa'

import { useItineraries } from 'contexts/itinerary.context'
import { getMapboxCountry } from 'utils/mapboxLocation'

import * as S from './style'

const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1IjoibGF1cmFoZGV2IiwiYSI6ImNsY3BtMTN4dDFueG0zcG81em41YXh4emIifQ.eGKakvFNW6mcmRuHze23mg'

const ACCEPTED_IMAGE_INPUT =
  '.jpg,.jpeg,.png,.webp,.gif,.avif,.heic,.heif,image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic,image/heif'

type Props = {
  dayIndex: number
  cityIndex: number
  city: any
  setFieldValue: (field: string, value: any) => void
  setUploadError?: (key: string, message?: string) => void
  removeCity: () => void
}

const getCityName = (feature: any) =>
  feature?.properties?.name ||
  feature?.properties?.name_preferred ||
  feature?.properties?.full_address ||
  ''

const getCityLabel = (feature: any) => {
  const name = getCityName(feature)
  const context = feature?.properties?.place_formatted
  return context ? `${name} — ${context}` : name
}

const DayCityEditor = ({
  dayIndex,
  cityIndex,
  city,
  setFieldValue,
  setUploadError,
  removeCity
}: Props) => {
  const { uploadFile } = useItineraries()
  const path = `dayPlan.${dayIndex}.cities.${cityIndex}`
  const selectedCityName = city?.name || ''
  const [cityName, setCityName] = useState(selectedCityName)
  const [options, setOptions] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const images = useMemo(() => city?.images ?? [], [city?.images])
  const cover = images?.[0]?.file

  useEffect(() => {
    setCityName(selectedCityName)
  }, [selectedCityName])

  useEffect(() => {
    const query = cityName.trim()
    if (query.length < 3 || query === selectedCityName) {
      setOptions([])
      setSearching(false)
      return undefined
    }

    const source = axios.CancelToken.source()
    const timer = window.setTimeout(async () => {
      setSearching(true)

      try {
        const response = await axios.get(
          'https://api.mapbox.com/search/geocode/v6/forward',
          {
            params: {
              q: query,
              types: 'place,locality',
              language: 'pt,de,en',
              autocomplete: true,
              limit: 8,
              access_token: MAPBOX_ACCESS_TOKEN
            },
            cancelToken: source.token
          }
        )
        setOptions(response.data?.features || [])
      } catch (error) {
        if (!axios.isCancel(error)) setOptions([])
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => {
      window.clearTimeout(timer)
      source.cancel('Nova busca iniciada')
    }
  }, [cityName, selectedCityName])

  const selectCity = (feature: any) => {
    const name = getCityName(feature)
    const coordinates = feature?.geometry?.coordinates || [0, 0]
    const { countryName, countryCode } = getMapboxCountry(feature)

    setCityName(name)
    setFieldValue(`${path}.name`, name)
    setFieldValue(`${path}.latitude`, Number(coordinates[1] || 0))
    setFieldValue(`${path}.longitude`, Number(coordinates[0] || 0))
    setFieldValue(`${path}.countryName`, countryName || '')
    setFieldValue(`${path}.countryCode`, countryCode || '')
    setOptions([])
  }

  const coverUploadKey = `${path}.coverUpload`
  const galleryUploadKey = `${path}.galleryUpload`

  const showUploadError = (key: string, error: any) => {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Não foi possível enviar a imagem.'
    const friendlyMessage = Array.isArray(message)
      ? message[0]
      : String(message)
    setUploadError?.(key, friendlyMessage)
    toast.error(friendlyMessage, { theme: 'dark' })
  }

  const replaceCover = async (file?: File) => {
    if (!file) return

    setUploadError?.(coverUploadKey)

    try {
      const uploaded = { file: await uploadFile(file, 'itinerary_photo') }
      setFieldValue(`${path}.images`, [uploaded, ...images.slice(1)])
    } catch (error: any) {
      showUploadError(coverUploadKey, error)
    }
  }

  const uploadImages = async (files: FileList | null) => {
    if (!files?.length) return

    setUploadError?.(galleryUploadKey)

    try {
      const uploaded = await Promise.all(
        Array.from(files).map(async (file) => ({
          file: await uploadFile(file, 'itinerary_photo')
        }))
      )
      setFieldValue(`${path}.images`, [...images, ...uploaded])
    } catch (error: any) {
      showUploadError(galleryUploadKey, error)
    }
  }

  const removeImage = (index: number) => {
    setFieldValue(
      `${path}.images`,
      images.filter((_: any, imageIndex: number) => imageIndex !== index)
    )
  }

  return (
    <S.CityEditorRow>
      <S.CityCover>
        {cover ? (
          <img src={cover} alt={cityName || 'Cidade'} />
        ) : (
          <S.CityCoverEmpty>
            <FaImage />
            <span>Sem foto</span>
          </S.CityCoverEmpty>
        )}
        <label>
          <FaCamera /> Foto
          <input
            type="file"
            accept={ACCEPTED_IMAGE_INPUT}
            onChange={(event) => replaceCover(event.target.files?.[0])}
          />
        </label>
      </S.CityCover>

      <S.CityField>
        <S.MiniLabel>
          {cityIndex === 0
            ? 'Cidade do dia'
            : `Parada adicional ${cityIndex + 1}`}
          {selectedCityName ? ` · ${selectedCityName}` : ''}
        </S.MiniLabel>
        <S.CitySearchWrap>
          <FaMapMarkerAlt />
          <input
            value={cityName}
            placeholder="Buscar cidade"
            onChange={(event) => {
              const value = event.target.value
              setCityName(value)
              if (value !== selectedCityName) {
                setFieldValue(`${path}.id`, '')
                setFieldValue(`${path}.name`, '')
                setFieldValue(`${path}.latitude`, 0)
                setFieldValue(`${path}.longitude`, 0)
                setFieldValue(`${path}.countryName`, '')
                setFieldValue(`${path}.countryCode`, '')
              }
            }}
          />
          {cityName && (
            <button
              type="button"
              aria-label="Limpar cidade"
              onClick={() => {
                setCityName('')
                setFieldValue(`${path}.id`, '')
                setFieldValue(`${path}.name`, '')
                setFieldValue(`${path}.latitude`, 0)
                setFieldValue(`${path}.longitude`, 0)
                setFieldValue(`${path}.countryName`, '')
                setFieldValue(`${path}.countryCode`, '')
              }}
            >
              <FaTimes />
            </button>
          )}
          {options.length > 0 && (
            <S.CityOptions>
              {options.map((option: any, index: number) => (
                <button
                  type="button"
                  key={option?.properties?.mapbox_id || option?.id || index}
                  onClick={() => selectCity(option)}
                >
                  {getCityLabel(option)}
                </button>
              ))}
            </S.CityOptions>
          )}
          {searching && <S.Searching>Buscando...</S.Searching>}
        </S.CitySearchWrap>
      </S.CityField>

      <S.GalleryField>
        <S.MiniLabel>Fotos da cidade</S.MiniLabel>
        <S.MiniGallery>
          {images.slice(1, 4).map((image: any, index: number) => (
            <S.GalleryThumb key={image.file || index}>
              <img src={image.file} alt="" />
              <button
                type="button"
                aria-label="Remover foto"
                onClick={() => removeImage(index + 1)}
              >
                <FaTimes />
              </button>
            </S.GalleryThumb>
          ))}
          <S.AddPhotoLabel>
            <FaPlus />
            <input
              type="file"
              accept={ACCEPTED_IMAGE_INPUT}
              multiple
              onChange={(event) => uploadImages(event.target.files)}
            />
          </S.AddPhotoLabel>
          {images.length > 4 && (
            <S.MorePhotos>+{images.length - 4}</S.MorePhotos>
          )}
        </S.MiniGallery>
      </S.GalleryField>

      <S.NotesField>
        <S.MiniLabel>Minhas anotações e experiências</S.MiniLabel>
        <textarea
          value={city?.description || ''}
          maxLength={5000}
          rows={12}
          style={{ minHeight: 260, resize: 'vertical', lineHeight: 1.6 }}
          placeholder="Conte o que viveu nesta cidade, os lugares que conheceu e, se quiser, onde se hospedou..."
          onChange={(event) =>
            setFieldValue(`${path}.description`, event.target.value)
          }
        />
        <small>{String(city?.description || '').length}/5000</small>
      </S.NotesField>

      <S.RemoveCityButton
        type="button"
        aria-label="Remover cidade"
        onClick={removeCity}
      >
        <FaTimes />
      </S.RemoveCityButton>
    </S.CityEditorRow>
  )
}

export default DayCityEditor
